import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2, AlertCircle, Clock } from 'lucide-react';
import { laborScheduleService } from '../../calendario/services/laborScheduleService';

function formatTime(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Santiago',
    });
  } catch {
    return '—';
  }
}

function formatHours(hours) {
  if (hours == null || Number.isNaN(hours)) return '—';
  return `${hours.toFixed(2)} h`;
}

function formatOrigin(origin) {
  if (!origin) return '—';
  if (origin === 'raw_system') return 'Sistema';
  return origin;
}

/**
 * Modal de detalle de tramos (entradas/salidas) de un empleado en un día concreto.
 * Muestra cada tramo entrada→salida por separado; útil cuando el empleado ha fichado
 * varias veces en el día (p. ej. pausa para comer).
 */
export function LaborAttendanceSegmentsModal({ open, employeeId, employeeName, date, onClose }) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['labor-attendance-segments', employeeId, date],
    queryFn: () => laborScheduleService.getAttendanceSegments(employeeId, date),
    enabled: open && Boolean(employeeId) && Boolean(date),
  });

  if (!open) return null;

  const segments = data?.segments ?? [];
  const isPending = data?.source === 'pending_raw';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 shrink-0 text-blue-400" />
            <h2 className="text-base font-semibold text-white">Detalle de tramos del día</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-400">
            {employeeName && (
              <p>
                Empleado: <span className="text-white font-medium">{employeeName}</span>
              </p>
            )}
            {date && (
              <p>
                Fecha: <span className="text-white font-mono">{date}</span>
              </p>
            )}
          </div>

          {(isLoading || isFetching) && (
            <div className="flex items-center gap-2 text-gray-400 py-6 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              Cargando tramos…
            </div>
          )}

          {!isLoading && error && (
            <div className="flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error.message || 'Error al cargar el detalle de tramos.'}</span>
            </div>
          )}

          {!isLoading && !error && (
            <>
              {isPending && (
                <p className="text-xs text-amber-400/90 bg-amber-900/20 border border-amber-700/50 rounded-lg px-3 py-2">
                  Este día aún no está validado: los tramos se calculan al vuelo a partir de los
                  fichajes brutos.
                </p>
              )}

              {segments.length === 0 ? (
                <p className="text-center text-gray-500 py-6 text-sm">
                  No hay fichajes registrados este día.
                </p>
              ) : (
                <div className="rounded-lg border border-gray-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-800/60 text-gray-400 text-xs uppercase tracking-wider">
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-center">Entrada</th>
                        <th className="px-3 py-2 text-center">Salida</th>
                        <th className="px-3 py-2 text-center">Duración</th>
                        <th className="px-3 py-2 text-center">Origen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {segments.map((seg) => (
                        <tr key={seg.sequence_number} className="hover:bg-gray-700/30 transition-colors">
                          <td className="px-3 py-2 text-gray-400 font-mono text-xs">
                            {seg.sequence_number}
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-xs text-emerald-400">
                            {formatTime(seg.clock_in)}
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-xs text-rose-400">
                            {formatTime(seg.clock_out)}
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-xs text-gray-300">
                            {formatHours(seg.duration_hours)}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-400">
                            {formatOrigin(seg.origin_in) === formatOrigin(seg.origin_out)
                              ? formatOrigin(seg.origin_in)
                              : `${formatOrigin(seg.origin_in)} / ${formatOrigin(seg.origin_out)}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="rounded-lg bg-gray-800/60 border border-gray-700 px-3 py-2 text-sm flex items-center justify-between">
                <span className="text-gray-400">Total del día</span>
                <span className="font-mono font-medium text-white">
                  {formatHours(data?.hours_worked)}
                </span>
              </div>

              {data?.incomplete && (
                <p className="text-xs text-amber-400/90">
                  Hay una entrada sin salida registrada todavía; el total puede aumentar.
                </p>
              )}
            </>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
